import React from 'react';
import PropTypes from 'prop-types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Dot
} from 'recharts';

const MiniTrajectoryChart = ({ 
  daysUntilNegative, 
  currentDay, 
  daysInMonth, 
  remainingBalance,
  projectedDrawdown 
}) => {
  // Generate trajectory data for Recharts
  const generateTrajectoryData = () => {
    const data = [];
    
    // If we don't have valid days until negative, just show current balance maintained
    if (!daysUntilNegative || daysUntilNegative < 0) {
      for (let day = currentDay; day <= daysInMonth; day++) {
        data.push({
          day,
          balance: remainingBalance,
          isToday: day === currentDay,
          isNegativePoint: false
        });
      }
      return data;
    }
    
    // Calculate daily spending rate based on when we'll go negative
    const dailySpendRate = remainingBalance / daysUntilNegative;
    
    for (let day = currentDay; day <= daysInMonth; day++) {
      const daysFromNow = day - currentDay;
      const projectedBalance = remainingBalance - (dailySpendRate * daysFromNow);
      
      data.push({
        day,
        balance: projectedBalance,
        isToday: day === currentDay,
        isNegativePoint: daysUntilNegative > 0 && day === currentDay + daysUntilNegative
      });
    }
    return data;
  };

  const trajectoryData = generateTrajectoryData();

  // Custom dot for special points
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    
    if (payload.isToday) {
      return <Dot cx={cx} cy={cy} r={4} fill="#6366f1" stroke="#6366f1" strokeWidth={2} />;
    }
    
    if (payload.isNegativePoint) {
      return <Dot cx={cx} cy={cy} r={5} fill="#ef4444" stroke="#ef4444" strokeWidth={2} className="animate-pulse" />;
    }
    
    return null;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-2 border border-gray-200 rounded shadow-lg text-xs">
          <p className="font-medium">Day {label}</p>
          <p className={`${data.balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
            Balance: ${Math.round(data.balance).toLocaleString()}
          </p>
          {data.isToday && <p className="text-blue-600 font-medium">Today</p>}
          {data.isNegativePoint && <p className="text-red-600 font-medium">Goes Negative</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-48">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={trajectoryData}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis 
            dataKey="day" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#64748b' }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickFormatter={(value) => `$${Math.round(value / 1000)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          
          {/* Zero reference line */}
          <ReferenceLine 
            y={0} 
            stroke="#ef4444" 
            strokeDasharray="4 4" 
            strokeWidth={1}
            opacity={0.7}
          />
          
          {/* Balance trajectory line */}
          <Line
            type="monotone"
            dataKey="balance"
            stroke={remainingBalance < 0 ? "#ef4444" : daysUntilNegative && daysUntilNegative < 14 ? "#f59e0b" : "#10b981"}
            strokeWidth={2}
            dot={<CustomDot />}
            activeDot={{ r: 6, fill: "#6366f1" }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

MiniTrajectoryChart.propTypes = {
  daysUntilNegative: PropTypes.number,
  currentDay: PropTypes.number.isRequired,
  daysInMonth: PropTypes.number.isRequired,
  remainingBalance: PropTypes.number.isRequired,
  projectedDrawdown: PropTypes.number.isRequired,
};

export default MiniTrajectoryChart;